import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../api';
import { LegalLinks } from '../components/LegalLinks';
import { LangSwitch, t, tn } from '../i18n';
import { emailOk } from './company';
import { useApi } from '../lib/useApi';
import { isBilltoro, markenName } from '../produkt';
import type { AppConfig } from '../types';

// Passwort vergessen: Link per E-Mail. Ohne E-Mail-Versand hilft nur die Kanzlei.
export function ForgotPage() {
  const config = useApi<AppConfig>('/api/config');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = `${t('Passwort vergessen')} · ${markenName()}`;
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!emailOk(email)) {
      setError(t('Bitte geben Sie Ihre E-Mail ein.'));
      document.getElementById('fg-mail')?.focus();
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/password/forgot', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const phone = config.data?.kanzleiPhone;
  const support = config.data?.supportEmail;
  return (
    <div className="auth">
      <div className="auth-box">
        <div className="auth-brand">
          <span className="logo" role="img" aria-label={markenName()} />
          <LangSwitch />
        </div>
        <h1>{t('Passwort vergessen')}</h1>
        {config.loading ? (
          <div className="spinner" aria-label={t('Lädt')} />
        ) : !config.data?.mailEnabled && isBilltoro() ? (
          // Billtoro ohne E-Mail-Versand (nur beim Einrichten, in der Produktion ist er Pflicht): keine Kanzlei
          <div className="card">
            <p style={{ margin: 0 }}>Bitte wenden Sie sich an unseren Support.</p>
            {support && (
              <p style={{ margin: '8px 0 0' }}>
                E-Mail: <a href={`mailto:${support}`}>{support}</a>
              </p>
            )}
          </div>
        ) : !config.data?.mailEnabled ? (
          <div className="card">
            <p style={{ margin: 0 }}>{t('Bitte wenden Sie sich an Ihre Kanzlei.')}</p>
            {phone && (
              <p style={{ margin: '8px 0 0' }}>
                {tn('Telefon: {phone}', { phone: <a href={`tel:${phone.replace(/\s+/g, '')}`}>{phone}</a> })}
              </p>
            )}
          </div>
        ) : sent ? (
          <p className="notice ok" role="status">
            {t(
              'Wenn es für diese E-Mail einen Zugang gibt, haben wir Ihnen einen Link geschickt. Bitte schauen Sie in Ihr Postfach.',
            )}
          </p>
        ) : (
          <>
            <p>{t('Wir schicken Ihnen einen Link, über den Sie ein neues Passwort festlegen.')}</p>
            <form className="card" onSubmit={submit} noValidate>
              <label className="field">
                <span>{t('E-Mail')}</span>
                <input
                  id="fg-mail"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'fg-err' : undefined}
                />
              </label>
              {error && (
                <p className="notice bad" role="alert" id="fg-err">
                  {error}
                </p>
              )}
              <button className="btn primary block" disabled={busy}>
                {busy ? t('Einen Moment …') : t('Link anfordern')}
              </button>
            </form>
          </>
        )}
        <p className="auth-foot">
          <Link to="/login">{t('Zur Anmeldung')}</Link>
        </p>
        <LegalLinks mitAgb />
      </div>
    </div>
  );
}
